import React from 'react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps } from '../../../types/second-screen.types';

function getDotClasses(isCurrent: boolean, isPast: boolean, isDark: boolean): string {
  if (isCurrent) return 'bg-primary border-primary animate-pulse shadow-[0_0_8px_rgba(0,199,196,0.5)]';
  if (isPast) return 'bg-primary/60 border-primary/60';
  if (isDark) return 'bg-transparent border-white/20';
  return 'bg-transparent border-slate-300';
}

function getConnectorClasses(isPast: boolean, isDark: boolean): string {
  if (isPast) return 'bg-primary/40';
  if (isDark) return 'bg-white/10';
  return 'bg-slate-200';
}

function getNameClasses(isCurrent: boolean, isPast: boolean, isDark: boolean): string {
  if (isCurrent) return 'text-primary';
  if (isPast) return isDark ? 'text-white/50' : 'text-slate-400';
  return isDark ? 'text-white/80' : 'text-slate-700';
}

const AgendaWidget: React.FC<WidgetProps> = () => {
  const { state, theme } = useSecondScreenData();
  const isDark = theme === 'dark';

  if (!state) return null;

  const { phases, currentPhaseIndex } = state;

  if (!phases.length) {
    return (
      <div className={`flex items-center justify-center h-full text-sm ${
        isDark ? 'text-white/30' : 'text-slate-400'
      }`}>
        No agenda
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full px-3 py-3 overflow-auto">
      {phases.map((phase, idx) => {
        const isCurrent = idx === currentPhaseIndex;
        const isPast = idx < currentPhaseIndex;

        const dotClasses = getDotClasses(isCurrent, isPast, isDark);
        const connectorClasses = getConnectorClasses(isPast, isDark);
        const nameClasses = getNameClasses(isCurrent, isPast, isDark);

        return (
          <div key={phase.id} className="flex gap-3 min-h-0">
            {/* Timeline column */}
            <div className="flex flex-col items-center shrink-0 w-4">
              <div className={`w-3 h-3 rounded-full shrink-0 border-2 transition-all ${dotClasses}`} />
              {idx < phases.length - 1 && (
                <div className={`w-0.5 flex-1 my-1 ${connectorClasses}`} />
              )}
            </div>

            {/* Content */}
            <div className="pb-4 min-w-0">
              <div className={`text-sm font-semibold leading-tight ${nameClasses}`}>
                {phase.name}
              </div>
              <div className={`text-xs mt-0.5 ${
                isDark ? 'text-white/30' : 'text-slate-400'
              }`}>
                {phase.duration}
              </div>
              {isCurrent && phase.objectives && (
                <div className={`text-xs mt-1 leading-snug ${
                  isDark ? 'text-white/50' : 'text-slate-500'
                }`}>
                  {phase.objectives}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AgendaWidget;
